import styled from '@xstyled/styled-components';
import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import { decodeHtmlEntities } from '~/lib/decodeHtmlEntities';
import { escapeHtml } from '~/lib/sanitization';
import { EditorSequence, SequenceItem, useEditorStore } from '~/shell/Store';
import { optionalFieldWrites } from '../text/optionalFieldWrites';
import { labelFieldOf, StepLabelFields } from './stepLabel';

type LabelField = keyof StepLabelFields;

/** Where each label field is saved: a QuestionField's value is its title. */
const COLUMN: Record<LabelField, string> = {
  subvalue: 'subtitle',
  value: 'title'
};

/** A QuestionField title takes 255 characters; a subtitle 512. */
const MAX_LENGTH: Record<LabelField, number> = { subvalue: 512, value: 255 };

// A panel of its own, so the editor's controls read apart from the slide,
// and stay above a step label too long for its segment (learners see it
// overflow the same way).
const Editor = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 25px;
  padding: 2px 12px 12px;
  border-radius: 12px;
  background-color: var(--color-white);
`;

const Line = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
`;

const Caption = styled.label`
  flex: none;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-grey-dk);
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  border: 1px solid var(--color-grey-md);
  border-radius: 8px;
  font: inherit;
  color: inherit;
  background-color: var(--color-grey-lt);
  &:focus {
    outline: none;
    border-color: var(--color-accent-dk);
    background-color: var(--color-white);
  }
`;

const hasText = (text: string) => Boolean(text.trim());

const findItem = (
  items: SequenceItem[] | undefined,
  id: string
): SequenceItem | undefined => {
  for (const item of items || []) {
    if (String(item.id) === id) return item;
    const found = findItem(item.items, id);
    if (found) return found;
  }
  return undefined;
};

/** A step's label fields as the author reads them: stored as HTML text, decoded once. */
const labelTextsOf = (sequence: EditorSequence | null, stepId: string) => {
  const step = findItem(sequence?.items, stepId);
  return {
    subvalue: decodeHtmlEntities(String(step?.subvalue ?? '')),
    value: decodeHtmlEntities(String(step?.value ?? ''))
  };
};

/**
 * The label of a gauge step, in the field learners see. Which field that is
 * follows what the server has accepted, so a refused clear stays here to try
 * again; each field saves alone, in order.
 */
const StepLabel = ({ stepId, number }: { stepId: string; number: number }) => {
  const sequence = useEditorStore(state => state.sequence);
  const updateSequence = useEditorStore(state => state.updateSequence);
  const texts = labelTextsOf(sequence, stepId);
  const writesOf = (field: LabelField) =>
    optionalFieldWrites(stepId, field, hasText(texts[field]), texts[field]);
  const [, redraw] = useReducer((count: number) => count + 1, 0);
  const [editing, setEditing] = useState<{
    field: LabelField;
    draft: string;
  } | null>(null);

  // Once a field's saves have settled, the editor's copy is the server's.
  useEffect(() => {
    writesOf('subvalue').seen(hasText(texts.subvalue), texts.subvalue);
    writesOf('value').seen(hasText(texts.value), texts.value);
    redraw();
  }, [stepId, texts.subvalue, texts.value]);

  useEffect(() => {
    const stops = (['subvalue', 'value'] as const).map(field =>
      writesOf(field).onSettled(redraw)
    );
    return () => stops.forEach(stop => stop());
  }, [stepId]);

  const field =
    editing?.field ??
    labelFieldOf({
      subvalue: writesOf('subvalue').hasText(),
      value: writesOf('value').hasText()
    });

  const leave = () => {
    if (!editing) return;
    const text = editing.draft.trim();
    const writes = writesOf(editing.field);
    const latest = writes.latestValue() ?? '';
    // Left as it was, trailing space and all, the label is not saved again.
    if (editing.draft !== latest && text !== latest) {
      const column = COLUMN[editing.field];
      writes.save(
        hasText(text),
        // Sent escaped, so the server's sanitizer keeps it as typed.
        () =>
          updateSequence(stepId, column, escapeHtml(text), 'QUESTION_FIELD'),
        text
      );
    }
    setEditing(null);
  };

  const id = `gauge-step-label-${stepId}`;
  return (
    <Line>
      <Caption htmlFor={id}>Step {number} label</Caption>
      <Input
        id={id}
        type="text"
        value={editing?.draft ?? texts[field]}
        maxLength={MAX_LENGTH[field]}
        placeholder="Add label"
        onFocus={() => setEditing({ field, draft: texts[field] })}
        onChange={e => setEditing({ field, draft: e.target.value })}
        onBlur={leave}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
    </Line>
  );
};

/**
 * The editor's controls for the chosen gauge step: its label and its
 * feedback. Learners see neither control, only what they hold.
 */
export const GaugeStepEditor = ({
  stepId,
  number,
  feedback
}: {
  stepId: string;
  number: number;
  feedback: ReactNode;
}) => (
  <Editor>
    <StepLabel key={stepId} stepId={stepId} number={number} />
    <Line role="group" aria-label={`Step ${number} feedback`}>
      <Caption as="span" aria-hidden="true">
        Step {number} feedback
      </Caption>
      <b>{feedback}</b>
    </Line>
  </Editor>
);

/** The label a gauge step shows in the editor, from the editor's copy. */
export const useEditorStepLabels = (
  steps: Array<{ id?: string }>,
  enabled: boolean
) => {
  const sequence = useEditorStore(state => (enabled ? state.sequence : null));
  return (index: number) => {
    const step = findItem(sequence?.items, String(steps[index]?.id));
    return { subvalue: step?.subvalue, value: step?.value } as {
      subvalue?: string | null;
      value?: string | null;
    };
  };
};
